import { color, HStack, isEnd, isEnter, isEscape, isHome, isLeftArrow, isRightArrow, Text } from '@starrez/starryui9';
import React, { createRef } from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import styled from 'styled-components';
import { GraphQLLoader } from '../../../../../../.starryUI/Loading/GraphQLLoader';
import { IMainDirectoryListVariables, useLazyMainDirectoryListQuery } from '../../../graphql/useAllMainDirectoryListQuery';
import { strings } from '../../../localization';
import { useViewPicker } from '../context/ViewPickerContext';
import { Item, ViewItem } from './ViewItem';
import { ItemContainer } from './ViewItem.styles';
import { ViewItemIcon } from './ViewItemIcon';
import { ViewItemList } from './ViewItemList';

export interface IViewItemFolder {
    id?: number;
    caption: string;
}

interface IViewItemFolderProps extends IViewItemFolder {
    activeFolder: string | null;
    divider?: boolean;
    setActiveFolder: (id: string | null) => void;
    listType: starrez.DirectoryListTypeEnum;
}

export const ViewItemFolder = ({ activeFolder, caption, divider, id, setActiveFolder, listType }: IViewItemFolderProps) => {
    const folderRef = createRef<HTMLLIElement>();
    const { closePicker } = useViewPicker();

    const variables: IMainDirectoryListVariables = {
        filter: [{ listType: { eq: listType } }, { folder: { eq: false } }],
        skip: 0,
        limit: 10,
        sort: [{
            field: 'DESCRIPTION',
            order: 'ASC',
        }],
    };

    const [getData, { data, loading }] = useLazyMainDirectoryListQuery(variables);
    const viewItems = data?.allMainDirectoryList?.nodes;

    const handleGetFolderItems = () => {
        if (activeFolder === `${caption}-${id}`) {
            setActiveFolder(null);
        } else {
            setActiveFolder(`${caption}-${id}`);
            getData();
        }
    };

    const handleOpenFolder = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (isRightArrow(e) || isEnter(e)) {
            handleGetFolderItems();
        } else if (isEscape(e)) {
            e.stopPropagation();
            closePicker();
        }
    };

    const handleCloseFolder = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (isLeftArrow(e) || isEscape(e)) {
            e.stopPropagation();
            (folderRef.current?.querySelector(`[tabindex="0"]:first-child`) as HTMLDivElement)?.focus();
            setActiveFolder(null);
        } else if (isHome(e)) {
            e.stopPropagation();
            (folderRef?.current?.lastElementChild?.firstElementChild?.firstElementChild?.firstElementChild as HTMLElement).focus();
        } else if (isEnd(e)) {
            e.stopPropagation();
            (folderRef?.current?.lastElementChild?.firstElementChild?.lastElementChild?.firstElementChild as HTMLElement).focus();
        }
    };

    const isFolderOpen = activeFolder === `${caption}-${id}`;

    return (
        <FolderItem role="option" ref={folderRef} style={{ position: 'relative' }} isFolder divider={divider}>
            <Manager>
                <Reference>
                    {({ ref }) => (
                        <Caption
                            ref={ref}
                            tabIndex={0}
                            aria-label={caption}
                            onClick={() => handleGetFolderItems()}
                            onKeyDown={(e) => handleOpenFolder(e)}
                        >
                            <HStack columns="1fr 36px" height="40px">
                                <Text variant="normal.regular">{caption}</Text>
                                <ViewItemIcon
                                    ariaLabel={isFolderOpen ? strings.View_Label_CloseList : strings.View_Label_OpenList}
                                    icon="CaretRight"
                                    title={isFolderOpen ? strings.View_Label_CloseList : strings.View_Label_OpenList}
                                    ariaExpanded={isFolderOpen ? true : false}
                                    ariaHaspopup="listbox"
                                />
                            </HStack>
                        </Caption>
                    )}
                </Reference>
                {isFolderOpen && (
                    <Popper placement="right-start" strategy="absolute">
                        {({ ref, style }) => (
                            <ListContainer ref={ref} style={{ ...style, zIndex: 2 }} onKeyDown={(e) => handleCloseFolder(e)}>
                                <GraphQLLoader loading={loading} error={undefined} loader="local">
                                    {() => (
                                        <ViewItemList autoFocus subList>
                                            {viewItems && viewItems.length > 0 ? (
                                                viewItems.map((itemData) => <ViewItem key={`view-item-${itemData.id}`} {...itemData} subItem />)
                                            ) : (
                                                <ItemContainer tabIndex={0}>
                                                    <Text variant="normal.regular">{strings.View_Label_NoItems}</Text>
                                                </ItemContainer>
                                            )}
                                        </ViewItemList>
                                    )}
                                </GraphQLLoader>
                            </ListContainer>
                        )}
                    </Popper>
                )}
            </Manager>
        </FolderItem>
    );
};

const Caption = styled.div`
    padding-left: 12px;
    outline: 0;

    &:hover {
        span {
            cursor: pointer;
        }
    }

    &:focus {
        box-shadow: 0 0 0 2px ${color.Blue.B80};
        border-radius: 4px;
    }
`;

interface IFolderProps {
    divider?: boolean;
}

const FolderItem = styled(Item)<IFolderProps>`
    &:hover > ${Caption} {
        color: ${color.Blue.B60};
    }

    ${({ divider }) =>
        divider &&
        `
        border-top: 1px solid ${color.Grey.G30};
    `}
`;

const ListContainer = styled.div`
    display: grid;
    align-items: center;
    min-height: 40px;
    min-width: 200px;
    background-color: white;
    border: 1px solid ${color.Grey.G30};
    box-shadow: 0px 2px 30px rgba(0, 0, 0, 0.1);
`;
